import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { CurrencyPipe, PercentPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { Observable, of, race, Subject, timer } from 'rxjs'
import { debounce, debounceTime, takeUntil } from 'rxjs/operators'
import { AppState } from '../../core/store'
import { selectCurrencyList } from '../../core/store/broker/broker.selectors'
import { analyzereConstants } from '../constants/analyzere'
import { Territories } from '../../api/territory/territory.service'
import { clone } from 'ramda'

interface EditReinstatements {
  id?: number
  counter: number
  premium?: number
  formattedValue?: string | number | number[]
}

export interface Reference {
  value: string
  viewValue: string | null
}

export interface DependentValue {
  parent: { id: string; value: string }
  child: { id: string; value: string }
}

type inputs = string | number | boolean | number[] | string[] | DependentValue

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-layer-property',
  styleUrls: ['./layer-property.component.scss'],
  templateUrl: './layer-property.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LayerPropertyComponent),
      multi: true,
    },
  ],
})
// tslint:disable: no-non-null-assertion
export class LayerPropertyComponent implements OnChanges, OnInit, OnDestroy, ControlValueAccessor {
  private destroy$ = new Subject()
  @Input() type:
    | 'text'
    | 'currency'
    | 'number-list'
    | 'dropdown'
    | 'dependent'
    | 'multiselect'
    | 'numeric'
    | 'checkbox'
    | 'unformatted-numeric'
    | 'textarea'
    | 'autocomplete'
    | 'date'
    | 'nondecimal-numeric'
    | 'empty'
  @Input() readonly = false
  @Input() error = false
  @Input() whiteLabel = false
  @Input() withDraggable = false
  @Input() isCession = false
  @Input() isReinstatements = false
  @Input() dontFilterList = false
  @Input() name: string
  @Input() value: string | string[] | number | number[] | boolean | DependentValue
  @Input() references?: Reference[]
  @Input() isNewLayer: boolean
  @Input() currentCurrency?: string
  @Input() numberTransform = (val: number) =>
    this.isValueUnlimited(val) ? analyzereConstants.unlimitedValue : val
  @Input() numberReverseTransform = (val: number) => val
  @Input() submitting = false
  @Input() inputChangeDebounceTime = 50000
  @Input() isQuotaShare = false
  @Input() cedingCommissionChecked = false
  @Input() subjectPremiumChecked = false
  @Input() decimal = '0'
  @Input() territories: Territories
  @Input() displayOverrideTooltip: boolean
  @Input() disableTooltipClick: boolean
  @Input() maxLength: number | null = null
  @Input() autofocusOnEnable = false

  get charCount(): number {
    return typeof this.formattedValue === 'string' ? this.formattedValue.length : 0
  }

  @Input() set hideLabel(value: any) {
    this._hideLabel = coerceBooleanProperty(value)
  }
  get hideLabel() {
    return this._hideLabel
  }
  _hideLabel = false

  @Input() set alignRight(value: any) {
    this._alignRight = coerceBooleanProperty(value)
  }
  get alignRight() {
    return this._alignRight
  }
  @HostBinding('class.align-right') _alignRight = false

  @Input() set cell(value: any) {
    this._cell = coerceBooleanProperty(value)
  }
  get cell() {
    return this._cell
  }
  @HostBinding('class.cell') _cell = false

  @Input() set checkboxRow(value: any) {
    this._checkboxRow = coerceBooleanProperty(value)
  }
  get checkbox_row() {
    return this._checkboxRow
  }

  @HostBinding('class.checkbox-row') _checkboxRow = false

  @Output() inputChange = new EventEmitter<
    string | number | boolean | number[] | string[] | DependentValue
  >()
  @Output() inputKeyup = new EventEmitter<void>()
  @Output() inputSubmit = new EventEmitter<void>()
  @Output() premiumValueChange = new EventEmitter<number>()
  @Output() cedingCommissionValueChange = new EventEmitter<number>()
  @Output() overrideTooltipClicked = new EventEmitter()

  @HostBinding('class.submitting') get isSubmitting() {
    return this.submitting
  }

  @ViewChild('textInput') textInput: ElementRef
  @ViewChild('textareaInput') textareaInput: ElementRef
  @ViewChild('percentageInput') percentageInput: ElementRef
  @ViewChild('currencyInput') currencyInput: ElementRef
  @ViewChild('numericInput') numericInput: ElementRef
  @ViewChild('unformattedNumericInput') unformattedNumericInput: ElementRef
  @ViewChild('dateInput') dateInput: ElementRef
  @ViewChild('nonDecimalNumericInput') nonDecimalNumericInput: ElementRef

  autoControl = new FormControl()
  currencyList$: Observable<any[]>
  formattedDate: Date
  items: Reference[]
  formattedValue: string | string[] | number | number[] | boolean | DependentValue
  transformedValue: string | string[] | number | number[] | boolean | DependentValue
  editReinstatements: EditReinstatements[]
  isReinstatementInput = false
  inputChangeDebounce$ = new Subject<string | string[] | number>()
  inputKeyupDebounce$ = new Subject<void>()
  focusOutSubject = new Subject<void>()
  isRemoveReinstatement = false

  get valueDropdown(): string {
    const value = this.items.find((i) => i.value === this.value)
    if (value && value.viewValue) {
      return value.viewValue
    }
    return 'Select'
  }
  get isDropdown(): boolean {
    return this.type === 'dropdown'
  }
  get isDependent(): boolean {
    return this.type === 'dependent'
  }
  get isMultiselect(): boolean {
    return this.type === 'multiselect'
  }
  get isAutocomplete(): boolean {
    return this.type === 'autocomplete'
  }
  get isCurrency(): boolean {
    return this.type === 'currency'
  }
  get isText(): boolean {
    return this.type === 'text'
  }
  get isEmpty(): boolean {
    return this.type === 'empty'
  }
  get isNumeric(): boolean {
    return this.type === 'numeric'
  }
  get isUnformattedNumeric(): boolean {
    return this.type === 'unformatted-numeric'
  }
  get isNonDecimalNumeric(): boolean {
    return this.type === 'nondecimal-numeric'
  }
  get isNumberList(): boolean {
    return this.type === 'number-list'
  }
  get isTextArea(): boolean {
    return this.type === 'textarea'
  }
  get isCheckbox(): boolean {
    return this.type === 'checkbox'
  }
  get isDate(): boolean {
    return this.type === 'date'
  }

  get tooltip(): string | number | number[] | undefined {
    return this.formattedValue && this.formatValue.length > 18 ? String(this.formattedValue) : ''
  }
  get spinnerSize(): number {
    return this.cell ? 16 : 32
  }

  get additionalMultiselectValues(): any {
    if (this.name === 'Territories') {
      return this.territories
    }
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private readonly currencyPipe: CurrencyPipe,
    private store: Store<AppState>,
  ) {}

  isNumberOrReinstatementArray = (value: number[] | string[]): value is number[] => {
    if (value && value.length > 0) {
      // @ts-ignore
      return !value.every((item: number | string) => typeof item === 'string')
    } else {
      return true
    }
  }

  ngOnInit(): void {
    if (this.isCheckbox) {
      this.value = !!this.value
    } else if (this.isText && this.readonly) {
      this.formattedValue = this.value
    } else if (!this.isMultiselect && !this.isDependent) {
      if (
        this.isNumberList &&
        Array.isArray(this.value) &&
        typeof this.value === 'object' &&
        this.isNumberOrReinstatementArray(this.value)
      ) {
        this.editReinstatements = this.formatValueReinstatement(this.value)
      } else {
        this.setNumericValues()
      }
    }
    if (this.isDropdown || this.isMultiselect || this.isAutocomplete) {
      if (this.value === 'Unknown') {
        this.items = [{ value: 'Unknown', viewValue: 'Unknown' }]
      } else {
        this.items = this.references as Reference[]
      }
      if (this.name === 'Structure FX' || this.name === 'Premium FX') {
        this.currencyDataUpdate()
      }
    }
    this.autoControl.valueChanges.subscribe((searchVal) => {
      if (this.items) {
        if (!this.dontFilterList) {
          this.items = this.filterItemsValues(searchVal) as Reference[]
        }
      }
    })

    this.inputChangeDebounce$
      .pipe(
        debounce(() => {
          if (this.isRemoveReinstatement) {
            return of(500)
          } else {
            return race(timer(this.inputChangeDebounceTime), this.focusOutSubject.asObservable())
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((val) => {
        const value =
          typeof this.value === 'number' && typeof val === 'number'
            ? this.numberReverseTransform(val)
            : val
        this.inputChange.emit(value)
        this.value = value
        this.onChange(value)
      })
    this.inputChangeDebounceTime = 50000
    this.inputKeyupDebounce$
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe((val) => this.inputKeyup.emit(val))
    this.isRemoveReinstatement = false
  }

  currencyDataUpdate(): void {
    this.currencyList$ = this.store.pipe(select(selectCurrencyList))
    this.currencyList$.pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      const data: Reference[] = []
      result.forEach((element: any) => {
        data.push({
          value: element.code,
          viewValue: element.code,
        })
      })
      this.items = data
      this.references = data
      this.cdRef.detectChanges()
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.autofocusOnEnable && changes.readonly && !changes.readonly.currentValue) {
      setTimeout(() => this.textInput?.nativeElement?.focus())
    }
    if (this.isDropdown) {
      if (
        changes.references &&
        JSON.stringify(changes.references.currentValue) !==
          JSON.stringify(changes.references.previousValue)
      ) {
        this.getList()
      }
      if (this.items.length <= 0) {
        this.getList()
      }
      return
    }
    if (changes.value) {
      this.autoControl.setValue({
        viewValue: this.value,
      })
      if (this.isDate && this.value && typeof this.value === 'string') {
        this.formattedDate = this.parseDateLocal(this.value)
      }
    }
    if (
      this.isNumberList &&
      typeof this.value === 'object' &&
      Array.isArray(this.value) &&
      this.isNumberOrReinstatementArray(this.value)
    ) {
      this.editReinstatements = this.formatValueReinstatement(this.value)
    } else if (!this.isCheckbox && !this.isMultiselect && !this.isDependent) {
      this.setNumericValues()
    }
    if (
      this.isQuotaShare &&
      this.subjectPremiumChecked &&
      this.name === 'Premium' &&
      typeof this.value === 'number' &&
      changes.value &&
      changes.value.currentValue !== changes.value.previousValue
    ) {
      this.premiumValueChange.emit(this.value)
    }
    if (
      this.isQuotaShare &&
      this.cedingCommissionChecked &&
      this.name === 'Ceding Commission' &&
      typeof this.value === 'number' &&
      changes.value &&
      changes.value.currentValue !== changes.value.previousValue
    ) {
      this.cedingCommissionValueChange.emit(this.value)
    }
    if (this.name === 'Final Signature Page Changes') {
      if (this.formattedValue === '0') {
        this.formattedValue = ''
        this.value = ''
      }
    }
    this.isRemoveReinstatement = false
  }

  trackByDropdownItem(_index: number, item: Reference): any {
    return item.value
  }

  multiselectInputChange(selectedValues: string[]): void {
    this.value = selectedValues
    this.inputChange.emit(selectedValues)
  }

  isValueUnlimited(num: number) {
    return String(num).toLowerCase().includes('e')
  }

  numberAction($event: any): void {
    let modifiedValue = $event
    switch (this.name) {
      case 'Premium FX to USD':
        const val = Number($event)
        modifiedValue = val.toFixed(6)
        break
      case 'Loss Scale Factor': // Warning: Loss Scale Factor shares same switch condition as New Expense Ratio... Leave it to together
      case 'New Expense Ratio':
        const currentValue = Number(modifiedValue)
        let value = parseFloat(currentValue.toString())
        value = value * 10 ** 5
        value = Math.round(value)
        value = value / 10 ** 5
        modifiedValue = value
        break
      case 'Premium Scale Factor':
        const factorValue = Number($event)
        modifiedValue = factorValue.toFixed(1)
        break
      default:
        if (modifiedValue) {
          const modValue = clone(modifiedValue)
          const arr = modValue.split('.')
          if (arr[1] && arr[1].length > 1) {
            const usedDecimals = Math.max(1, arr[1].length)
            const decimal = Math.min(Number(this.decimal), usedDecimals)
            modifiedValue = Number(modifiedValue).toFixed(decimal)
          }
        }
        break
    }
    this.inputChangeDebounce$.next(modifiedValue)
  }

  get overrideTooltip(): string {
    return `This value has been overwritten and is not connected to modeling. Must be manually updated ${this.disableTooltipClick ? '(will reset on save)' : '(click to reset)'}.`
  }

  tooltipClicked(): void {
    if (!this.disableTooltipClick) {
      this.overrideTooltipClicked.emit()
    }
  }

  onFocusOut(): void {
    this.focusOutSubject.next()
    this.inputSubmit.emit()
  }

  onMouseDown(event: Event): void {
    if (this.withDraggable) {
      // stop mousedown event from bubbling up to parent elements; allows user to drag/highlight input without moving a draggable panel
      event.stopPropagation()
    }
  }

  blur(): void {
    const els = [
      this.textInput,
      this.textareaInput,
      this.percentageInput,
      this.currencyInput,
      this.numericInput,
      this.unformattedNumericInput,
      this.dateInput,
      this.nonDecimalNumericInput,
    ]
    els
      .filter((el) => el != null)
      .map((el) => el.nativeElement as HTMLInputElement)
      .forEach((el) => el.blur())
  }

  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  getList(): void {
    this.items = this.references as Reference[]
  }

  editedReinstatement(): void {
    const newReinstatement: Array<any> = []
    // Will have to delete this code once confirmed.
    // this.editReinstatements = this.collapseReinstatements(
    //   this.editReinstatements
    // )
    this.editReinstatements.forEach((er) => {
      er.formattedValue = this.setPipePercentage(er.premium)
      if (er.counter!.toString() === '') {
        newReinstatement.push({ premium: Number(''), brokerage: 0 })
      } else {
        for (let x = 0; x < er.counter!; x++) {
          newReinstatement.push({
            // id: er.id,
            premium: er.premium === undefined ? undefined : Number(er.premium),
            brokerage: 0,
          })
        }
      }
    })
    this.inputChangeDebounce$.next(newReinstatement)
    this.inputKeyupDebounce$.next()
  }

  addReinstatement(): void {
    this.editReinstatements.push({
      premium: undefined,
      counter: 0,
      formattedValue: this.setPipePercentage(undefined),
    })
    const newReinstatement: Array<any> = []
    this.editReinstatements.forEach((er) => {
      for (let x = 0; x < er.counter!; x++) {
        newReinstatement.push({
          // id: er.id,
          premium: er.premium === undefined ? undefined : Number(er.premium),
          brokerage: 0,
        })
      }
    })
    this.inputChangeDebounce$.next(newReinstatement)
    this.inputKeyupDebounce$.next()
  }

  removeReinstatement(index: number): void {
    this.editReinstatements.splice(index, 1)
    const newReinstatement: Array<any> = []
    this.editReinstatements.forEach((er) => {
      for (let x = 0; x < er.counter!; x++) {
        newReinstatement.push({
          // id: er.id,
          premium: er.premium === undefined ? undefined : Number(er.premium),
          brokerage: 0,
        })
      }
    })
    this.isRemoveReinstatement = true
    this.inputChangeDebounce$.next(newReinstatement)
    this.inputKeyupDebounce$.next()
  }

  private formatValueReinstatement(value: number[] | any[]): EditReinstatements[] {
    const newArray: Array<EditReinstatements> = []
    value.forEach((r: any | number) => {
      if (typeof r === 'number') {
        throw new Error('Layer property number-list value must be reinstatements')
      }
      if (newArray.length > 0 && newArray[newArray.length - 1].premium === r.premium) {
        newArray[newArray.length - 1].counter! += 1
      } else {
        newArray.push({
          // id: r.id,
          counter: 1,
          premium: r.premium,
          formattedValue: this.setPipePercentage(r.premium),
        })
      }
    })
    return newArray
  }

  onMenuItemSelected(value: Reference): void {
    this.value = value.value
    this.inputChange.emit(value.value)
    this.onChange(value.value)
    this.inputSubmit.next()
  }

  onDateChange(date: Date): void {
    if (this.formattedDate && !date) {
      this.formattedDate = new Date(this.formattedDate) // ensures date is not cleared if already set
      return
    }
    // Emit a date-only string so downstream storage and parseDateLocal both see
    // the same local calendar date regardless of the user's UTC offset.
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    this.value = `${yyyy}-${mm}-${dd}`
    this.inputChange.emit(this.value)
    this.onChange(this.value)
    this.inputSubmit.next()
  }

  setPipeCurrency(num: string | number): string {
    if (num !== '0') {
      if (
        num.toString().indexOf('e+') > -1 ||
        num.toString() === 'Unlimited' ||
        (!isNaN(Number(num)) &&
          Number(num) >= this.numberTransform(analyzereConstants.unlimitedValue))
      ) {
        return 'Unlimited'
      } else {
        try {
          const curr = this.currencyPipe.transform(
            num,
            this.currentCurrency || '',
            'symbol-narrow',
            `1.${this.decimal}-${this.decimal}`,
          )
          return curr || ''
        } catch (e) {
          this.inputChangeDebounce$.next(0)
          this.inputKeyupDebounce$.next()
          return (
            this.currencyPipe.transform(
              0,
              this.currentCurrency || '',
              'symbol-narrow',
              `1.${this.decimal}-${this.decimal}`,
            ) || ''
          )
        }
      }
    } else {
      const curr = this.currencyPipe.transform(
        num,
        this.currentCurrency || '',
        'symbol-narrow',
        `1.${this.decimal}-${this.decimal}`,
      )
      return curr || ''
    }
  }

  setPipePercentage(num: string | number | undefined): string {
    if (typeof num === undefined) {
      return ''
    }
    const pipe = new PercentPipe('en-US')
    let perc
    if (this.isCession) {
      perc = pipe.transform(num, '1.1-1')
    } else {
      perc = pipe.transform(num)
    }
    return perc || ''
  }

  currencyInputChanged(rawValue: number | string): number | undefined {
    if (rawValue) {
      if (typeof rawValue === 'string') {
        if (rawValue.match(/unlimited/i)) {
          this.value = analyzereConstants.unlimitedValue
          return this.value
        }
      }
      // Remove the currency symbol and grouping separators such as commas
      const digits = String(rawValue).replace(/[^.\d]/g, '')

      this.value = Number(digits)

      return this.value
    }
  }

  percentInputChanged(value: any): number {
    if (value) {
      const num = value.replace(/[%,]/g, '')
      const converted = num ? Number(num / 100) : 1
      if (this.isCession || this.isNumberList) {
        return isNaN(converted) ? 0 : converted
      } else {
        if (converted > 1) {
          return 1
        } else {
          return isNaN(converted) ? 0 : converted
        }
      }
    } else {
      return 0
    }
  }

  nonDecimalNumericChanged(value: number) {
    if (value) {
      return Number(value).toFixed(0)
    } else {
      return 0
    }
  }

  private setNumericValues(): void {
    if (this.isCheckbox) {
      return
    }
    if (typeof this.value !== 'number' && typeof this.value !== 'string') {
      this.value = 0.0
    }
    const transformedValue =
      typeof this.value === 'number' ? this.numberTransform(this.value) : this.value

    this.formattedValue = this.formatValue(String(transformedValue))
    this.transformedValue = transformedValue
  }

  private formatValue(value: string | number): string {
    switch (this.type) {
      case 'currency':
        return this.setPipeCurrency(value)
      case 'text':
        return value.toString()
      case 'dropdown':
        return value.toString()
      case 'numeric':
        if (this.name === 'Model Version') {
          return Number(value).toFixed(1)
        } else {
          return value.toString()
        }
      case 'checkbox':
        return value.toString()
      case 'textarea':
        return value.toString()
      case 'nondecimal-numeric':
        return Number(value).toFixed(0)
      default:
        return ''
    }
  }

  filterItemsValues(searchVal: any): Reference[] | undefined {
    if (this.references) {
      if (searchVal.viewValue) {
        return this.references.filter((value) => {
          if (value.viewValue) {
            return value.viewValue.toLowerCase().indexOf(searchVal.viewValue.toLowerCase()) === 0
          }
        })
      } else {
        return this.references.filter((value) => {
          if (value.viewValue) {
            return value.viewValue.toLowerCase().indexOf(searchVal.toLowerCase()) === 0
          }
        })
      }
    }
  }

  displayFn(item: any): string | undefined {
    return item ? item.viewValue : undefined
  }

  getType(): string {
    if (this.name && this.name === 'New Expense Ratio') {
      return 'text'
    }
    return 'number'
  }

  // These are the callbacks that Angular will register
  private onChange: (value: inputs) => void = (value) => {}
  private onTouched: () => void = () => {}

  // Implement required methods from ControlValueAccessor
  private parseDateLocal(iso: string): Date {
    // Date-only strings (YYYY-MM-DD) are constructed as local midnight so they
    // are not shifted by the UTC offset (new Date('2026-06-30') parses as UTC
    // midnight, which is June 29 at 23:00 in UTC+1).
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [year, month, day] = iso.split('-').map(Number)
      return new Date(year, month - 1, day)
    }
    // Full ISO timestamps are passed directly to Date so JS resolves them to
    // the correct local instant (e.g. 2026-06-29T23:00:00.000Z → June 30 in UTC+1).
    return new Date(iso)
  }

  writeValue(value: inputs): void {
    this.value = value // Update the component's value when Angular updates it
    this.setNumericValues()
    if (this.isDate && value && typeof value === 'string') {
      this.formattedDate = this.parseDateLocal(value)
    }
    this.cdRef.detectChanges()
  }

  registerOnChange(fn: (value: inputs) => void): void {
    this.onChange = fn // Register Angular's onChange callback
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn // Register Angular's onTouched callback
  }

  setDisabledState(isDisabled: boolean): void {
    this.readonly = isDisabled
    this.cdRef.detectChanges()
  }
}
